import { create } from 'zustand';

/**
 * FRONT-2387: shared, cross-component state for the "audio answers" feature
 * — a play control on every finished assistant answer, click-only (see
 * `audio-answer-text.ts`/`audio-answer-player.ts` for the pure text and
 * playback halves). One store app-wide, following the same plain-object
 * `create()` pattern as `~/lib/stores/chat-open-store.ts`.
 *
 * Holds exactly one thing: which message (if any) is currently
 * loading/playing, app-wide. There is no "pending"/"ready" bookkeeping —
 * that only existed for the old dictation-triggered flow. Every finished
 * answer with non-empty speakable text gets a control, unconditionally
 * (behind the flag), so nothing needs marking in advance; `AudioAnswerButton`
 * derives its own visibility straight from the message it renders.
 *
 * `playing` is keyed by `playKeyOf(sessionId, renderKey)` —
 * `renderKeyOf(message)` (`hooks/reconcile-history.ts`) is what survives the
 * post-stream history reload's id promotion (a streamed UUID swapped for the
 * persisted numeric row id); keying on the raw message id would make "is
 * THIS message playing" go stale the instant that swap lands, mid-playback.
 * Session-scoped so the same render key can never collide across two
 * different conversations.
 */

export type AudioAnswerPlaybackState = 'loading' | 'playing' | 'done' | 'stopped' | 'error';

export interface AudioAnswerPlaying {
  key: string;
  state: AudioAnswerPlaybackState;
}

/** The key `playing` is tracked under: session id plus a message's render
 *  key. `AudioAnswerButton` computes the identical key from the message it
 *  renders, so the two sides agree regardless of which id (streamed or
 *  persisted) is on screen at the time. */
export function playKeyOf(sessionId: string, renderKey: string): string {
  return `${sessionId}:${renderKey}`;
}

/** The new `playing` value for a key entering `playState` — always REPLACES
 *  whatever was there, which is what enforces "only one at a time" at the
 *  state level (the player enforces it at the audio-hardware level, by
 *  stopping the previous handle before starting a new one). */
export function setPlayingTransition(
  key: string,
  playState: AudioAnswerPlaybackState,
): AudioAnswerPlaying {
  return { key, state: playState };
}

interface AudioAnswerState {
  playing: AudioAnswerPlaying | null;
  setPlaying: (key: string, state: AudioAnswerPlaybackState) => void;
  clearPlaying: () => void;
  /** Test-only full reset — mirrors the pattern other stores' tests use via
   *  `setState` directly, exposed as an action so a caller need not know the
   *  store's field shape. */
  reset: () => void;
}

export const useAudioAnswerStore = create<AudioAnswerState>((set) => ({
  playing: null,
  setPlaying: (key, playState) => set({ playing: setPlayingTransition(key, playState) }),
  clearPlaying: () => set({ playing: null }),
  reset: () => set({ playing: null }),
}));
